import { createColumnHelper } from '@tanstack/react-table';
import { ChevronRightIcon } from 'lucide-react';
import { useMemo } from 'react';

import type { SkuDTO, SkuType } from '@invenco/products-interface';

import { Badge } from '@/components/core/badge';
import { Card, CardHeader, CardTitle } from '@/components/core/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/core/collapsible';
import { DataTable, useDataTable, useDataTableState } from '@/components/data-table';
import { skuTypeTitle } from '@/shared/title-maps';

import { SkuBadge } from '../../components/sku-badge';

type Props = {
  sku?: SkuDTO;
  isLoading?: boolean;
};

type AssemblyRow = {
  id: string;
  skuName: string;
  skuDescription?: string | null;
  skuType: SkuType;
};

const columnHelper = createColumnHelper<AssemblyRow>();

const columns = [
  columnHelper.accessor('skuName', {
    header: 'SKU / Description',
    cell: ({ getValue, row }) => (
      <div className="flex min-w-0 items-center gap-2">
        <SkuBadge name={getValue()} type={row.original.skuType} />
        {row.original.skuDescription ? (
          <span className="min-w-0 truncate text-sm">{row.original.skuDescription}</span>
        ) : null}
      </div>
    ),
    meta: { truncate: true },
  }),
  columnHelper.accessor('skuType', {
    header: 'Type',
    cell: ({ getValue }) => <Badge variant="secondary">{skuTypeTitle[getValue()]}</Badge>,
  }),
];

export function AssembliesCard({ sku, isLoading }: Props) {
  const rows = useMemo<AssemblyRow[]>(
    () =>
      sku?.assemblies?.map(({ assemblySku }) => ({
        id: assemblySku.id,
        skuName: assemblySku.name,
        skuDescription: assemblySku.description,
        skuType: assemblySku.type,
      })) ?? [],
    [sku?.assemblies],
  );

  const hasRows = rows.length > 0;

  const { state, onSortingChange } = useDataTableState({
    columns,
    tableId: 'sku-assemblies-table',
    defaultPageSize: 10,
  });

  const table = useDataTable({
    data: rows,
    columns,
    state,
    isLoading,
    manualPagination: true,
    manualSorting: false,
    onSortingChange,
  });

  const titleText = hasRows ? 'Used as component' : 'Not used as component';

  return (
    <Card aria-label={titleText}>
      {hasRows ? (
        <Collapsible>
          <CollapsibleTrigger className="group/assemblies flex w-full cursor-pointer">
            <CardHeader className="flex-1">
              <CardTitle className="flex items-center gap-2">
                <ChevronRightIcon className="size-5 text-muted-foreground transition-transform duration-200 group-hover/assemblies:text-foreground group-data-[state=open]/assemblies:rotate-90" />
                <span>{titleText}</span>
                <Badge variant="secondary" aria-label={`with ${table.getRowCount()} rows`}>
                  {table.getRowCount()}
                </Badge>
              </CardTitle>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent className="overflow-hidden">
            <DataTable table={table} className="border-t" />
          </CollapsibleContent>
        </Collapsible>
      ) : (
        <CardHeader>
          <CardTitle>{titleText}</CardTitle>
        </CardHeader>
      )}
    </Card>
  );
}
